import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useEffect, useRef, useState } from 'react';
import { BILL_ALLOCATION_STATE } from 'src/api/billAutomation/bills';
import { useStore } from 'src/stores';

/** The allocation is worked out after the call that starts it, so this is how often we ask whether it is done. */
const POLL_INTERVAL_MS = 4000;

/** Roughly three minutes. Past that something is wrong, and asking forever would not fix it. */
const MAX_POLLS = 45;

// `readBillId`: the bill an answer about its services has come back for. Until one has, nobody can tell a table
// awaiting approval from one nobody has allocated (LB-6052), and another bill's answer says nothing about this one.
const EMPTY_STATE = {
  error: null,
  isBusy: false,
  readBillId: null,
  services: []
};
const ALLOCATION_UNREACHABLE = 'The allocation could not be reached. Please try again.';
const ALLOCATION_SLOW = 'The allocation is taking longer than usual. It is still being worked out — close this bill and open it again to ' + 'see where it got to.';

/**
 * Where each service of a bill has got to on its way to being allocated, and the two steps that move it along
 * (LB-6052). `allocate` sends the values as they stand on screen; the allocation service creates the bill and then
 * works the table out, and stops at "Awaiting approval". `approve` is the separate click that charges the residents.
 *
 * While a table is being worked out this keeps reading where it has got to. Only reading: the poll never allocates
 * or approves anything, so opening a bill can never charge anybody. It stops once nothing is left calculating — a
 * table awaiting approval is not calculating, it is waiting for a person.
 */
export function useBillAllocations(billId) {
  const store = useStore();
  const _useState = useState(EMPTY_STATE),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  const _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    polls = _useState4[0],
    setPolls = _useState4[1];

  // Which bill the answers still coming back belong to, and whether anybody is left to receive
  // them. A request outlives the effect that started it, and counting a poll re-runs that effect,
  // so the effect's own cleanup cannot be what decides whether its answer is still wanted.
  const askedAbout = useRef(billId);
  const isListening = useRef(true);
  // How many allocates and approves have been sent. A read sent before one of them can come back after it, and
  // would put the table back where it stood before the click — an approved table offered for approval again.
  const sentSteps = useRef(0);
  useEffect(() => () => {
    isListening.current = false;
  }, []);

  /** True while an answer about this bill is still worth acting on. */
  const isWanted = useCallback(forBillId => isListening.current && askedAbout.current === forBillId, []);
  const apply = useCallback((forBillId, resp) => {
    if (!isWanted(forBillId)) return;

    // A request that was cancelled, or failed (the store answers a failed one with nothing too), says nothing. Once
    // the bill has been read, what is on screen is still true. Before, nothing is known about its services: that is
    // said, rather than the tables calculating for ever.
    if (!resp) {
      setState(current => _objectSpread(_objectSpread({}, current), {}, {
        error: current.readBillId === forBillId ? current.error : ALLOCATION_UNREACHABLE,
        isBusy: false,
        readBillId: forBillId
      }));
      return;
    }
    if (!resp.isSuccessful || !resp.response) {
      setState(current => _objectSpread(_objectSpread({}, current), {}, {
        error: ALLOCATION_UNREACHABLE,
        isBusy: false,
        readBillId: forBillId
      }));
      return;
    }
    setState({
      error: null,
      isBusy: false,
      readBillId: forBillId,
      services: resp.response.services || []
    });
  }, [isWanted]);
  const fail = useCallback(forBillId => {
    if (!isWanted(forBillId)) return;
    setState(current => _objectSpread(_objectSpread({}, current), {}, {
      error: ALLOCATION_UNREACHABLE,
      isBusy: false,
      readBillId: forBillId
    }));
  }, [isWanted]);
  useEffect(() => {
    askedAbout.current = billId;
    // Another bill's services are not this one's, however briefly.
    setState(EMPTY_STATE);
    setPolls(0);
    if (!billId) return;
    const stepsBefore = sentSteps.current;
    const isCurrent = () => sentSteps.current === stepsBefore;
    store.companyBills.getBillAllocationsAsync.run({
      billId
    }).then(resp => isCurrent() && apply(billId, resp)).catch(() => isCurrent() && fail(billId));
  }, [apply, billId, fail, store]);

  // Only a table still being worked out moves on its own. One awaiting approval waits for somebody to approve it,
  // and asking again would only read the same thing until the poll gave up. Not one allocated in Apply Billing,
  // which is worked out and approved there (LB-5952).
  const isCalculating = state.services.some(service => !service.isMadeInApplyBilling && service.state === BILL_ALLOCATION_STATE.allocating);
  const hasGivenUp = polls >= MAX_POLLS;
  useEffect(() => {
    // Not while an allocate or approve is on its way: its answer is newer than anything a poll could read now.
    if (!billId || !isCalculating || hasGivenUp || state.isBusy) return undefined;
    const timer = setTimeout(() => {
      const stepsBefore = sentSteps.current;
      const isCurrent = () => sentSteps.current === stepsBefore;

      // Silent: a background re-read that fails is asked again on the next interval, not raised every four seconds.
      store.companyBills.getBillAllocationsAsync.run({
        billId
      }, undefined, {
        silent: true
      }).then(resp => isCurrent() && apply(billId, resp)).catch(() => isCurrent() && fail(billId))
      // Counted once the answer is in, not when the request goes out: this is also what re-arms
      // the loop when a poll comes back saying the same thing as the last one.
      .finally(() => isWanted(billId) && setPolls(count => count + 1));
    }, POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [apply, billId, fail, hasGivenUp, isCalculating, isWanted, polls, state.isBusy, store]);
  useEffect(() => {
    if (!hasGivenUp || !isCalculating) return;
    setState(current => current.error ? current : _objectSpread(_objectSpread({}, current), {}, {
      error: ALLOCATION_SLOW
    }));
  }, [hasGivenUp, isCalculating]);

  /** Sends one allocate or approve and shows where every service stands afterwards. */
  const send = useCallback(async request => {
    sentSteps.current += 1;
    setPolls(0);
    setState(current => _objectSpread(_objectSpread({}, current), {}, {
      error: null,
      isBusy: true
    }));
    try {
      const resp = await request();
      apply(billId, resp);
      // No answer (the request failed or was cancelled) confirms nothing on screen, and a read dropped while it was
      // out may have been the only one this bill had: read again rather than leave every table looking unallocated.
      if (!resp && isWanted(billId)) {
        const reread = await store.companyBills.getBillAllocationsAsync.run({
          billId
        }, undefined, {
          silent: true
        });
        if (reread) apply(billId, reread);
      }
    } catch (_unused) {
      fail(billId);
    }
  }, [apply, billId, fail, isWanted, store]);

  /**
   * `confirmedOldServicePeriod` says the person confirmed the warning that a service period on the bill is long past
   * (LB-6030); the allocation records who and when.
   */
  const allocate = useCallback(async function (services) {
    let _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      _ref$confirmedOldServ = _ref.confirmedOldServicePeriod,
      confirmedOldServicePeriod = _ref$confirmedOldServ === void 0 ? false : _ref$confirmedOldServ;
    if (!billId || !(services !== null && services !== void 0 && services.length)) return;
    const body = confirmedOldServicePeriod ? {
      billId,
      confirmedOldServicePeriod,
      services
    } : {
      billId,
      services
    };
    await send(() => store.companyBills.allocateBillAsync.run(body));
  }, [billId, send, store]);

  /**
   * Approves the tables named, one `{ propertyId, utilityId }` each, and charges the residents on them. A table still
   * being worked out after it is followed by the poll as before.
   */
  const approve = useCallback(async tables => {
    if (!billId || !(tables !== null && tables !== void 0 && tables.length)) return;
    await send(() => store.companyBills.approveBillAsync.run({
      billId,
      tables
    }));
  }, [billId, send, store]);
  return {
    allocate,
    approve,
    error: state.error,
    isBusy: state.isBusy,
    isCalculating: isCalculating && !hasGivenUp,
    // A bill not saved yet has no services to read, so there is nothing to wait for. Read for this bill, not the last
    // one: the render after another bill is opened still holds the last one's answer.
    isRead: !billId || state.readBillId === billId,
    services: state.services
  };
}